/**
 * LibraryManager — CRUD for the `libraries` table plus driver dispatch.
 *
 * Wraps a LocalIndex handle (sharing its db) and exposes the user-facing
 * library lifecycle: add / list / get / setDefault / rename / update /
 * remove. Also owns ensurePersonalLibrary() — the silent auto-init on first
 * `skaile asset migrate`.
 *
 * @docLink packages/library/concepts#library-manager
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";
import { eq } from "drizzle-orm";
import type { LibrarySyncDriver } from "../sync/driver.js";
import { GitSyncDriver } from "../sync/git-driver.js";
import { LocalSyncDriver } from "../sync/local-driver.js";
import {
  type AddLibraryInput,
  type BackendConfig,
  DuplicateLibraryNameError,
  type Library,
  type LibraryBackend,
  type UpdateLibraryInput,
  UserLibraryNotFoundError,
} from "../user-library.js";
import type { LocalIndex } from "./library.js";
import * as schema from "./schema.js";

function rowToLibrary(row: typeof schema.libraries.$inferSelect): Library {
  const cfg = (() => {
    try {
      return JSON.parse(row.backendConfig) as BackendConfig;
    } catch {
      return {} as BackendConfig;
    }
  })();
  return {
    id: row.id,
    name: row.name,
    path: row.path,
    backend: row.backend as LibraryBackend,
    backendConfig: cfg,
    ownership: row.ownership as Library["ownership"],
    structure: (row.structure as Library["structure"]) ?? undefined,
    isDefault: row.isDefault,
    manifestGenerated: row.manifestGenerated,
    lastSyncAt: row.lastSyncAt ? new Date(row.lastSyncAt) : undefined,
    createdAt: new Date(row.createdAt),
    updatedAt: new Date(row.updatedAt),
  };
}

export class LibraryManager {
  private readonly localDrv = new LocalSyncDriver();
  private readonly gitDrv = new GitSyncDriver();

  constructor(private readonly lib: LocalIndex) {}

  /** Resolve the sync driver for a given backend. */
  driverFor(backend: LibraryBackend): LibrarySyncDriver {
    switch (backend) {
      case "local":
        return this.localDrv;
      case "git":
        return this.gitDrv;
    }
  }

  async addLibrary(input: AddLibraryInput): Promise<Library> {
    const db = await this.lib.dbHandle();
    const existing = await db
      .select()
      .from(schema.libraries)
      .where(eq(schema.libraries.name, input.name))
      .get();
    if (existing) throw new DuplicateLibraryNameError(input.name);

    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    // When isDefault=true, clear any other default first.
    if (input.isDefault) {
      await db.update(schema.libraries).set({ isDefault: false }).run();
    }
    await db
      .insert(schema.libraries)
      .values({
        id,
        name: input.name,
        path: input.path,
        backend: input.backend,
        backendConfig: JSON.stringify(input.backendConfig ?? {}),
        ownership: input.ownership,
        structure: input.structure ?? null,
        isDefault: input.isDefault ?? false,
        manifestGenerated: input.manifestGenerated ?? false,
        createdAt: now,
        updatedAt: now,
      })
      .run();
    return this.requireLibrary(id);
  }

  async listLibraries(): Promise<Library[]> {
    const db = await this.lib.dbHandle();
    const rows = await db.select().from(schema.libraries).all();
    return rows.map(rowToLibrary).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }

  async getLibrary(nameOrId: string): Promise<Library | null> {
    const db = await this.lib.dbHandle();
    let row = await db
      .select()
      .from(schema.libraries)
      .where(eq(schema.libraries.name, nameOrId))
      .get();
    if (!row) {
      row = await db.select().from(schema.libraries).where(eq(schema.libraries.id, nameOrId)).get();
    }
    return row ? rowToLibrary(row) : null;
  }

  async requireLibrary(nameOrId: string): Promise<Library> {
    const lib = await this.getLibrary(nameOrId);
    if (!lib) throw new UserLibraryNotFoundError(nameOrId);
    return lib;
  }

  async updateLibrary(nameOrId: string, patch: UpdateLibraryInput): Promise<Library> {
    const lib = await this.requireLibrary(nameOrId);
    const db = await this.lib.dbHandle();
    const updates: Partial<typeof schema.libraries.$inferInsert> = {
      updatedAt: new Date().toISOString(),
    };
    if (patch.name !== undefined) updates.name = patch.name;
    if (patch.path !== undefined) updates.path = patch.path;
    if (patch.backend !== undefined) updates.backend = patch.backend;
    if (patch.backendConfig !== undefined)
      updates.backendConfig = JSON.stringify(patch.backendConfig);
    if (patch.ownership !== undefined) updates.ownership = patch.ownership;
    if (patch.structure !== undefined) updates.structure = patch.structure;
    if (patch.manifestGenerated !== undefined) updates.manifestGenerated = patch.manifestGenerated;
    if (patch.isDefault !== undefined) updates.isDefault = patch.isDefault;
    if (patch.lastSyncAt !== undefined) updates.lastSyncAt = patch.lastSyncAt.toISOString();
    await db.update(schema.libraries).set(updates).where(eq(schema.libraries.id, lib.id)).run();
    return this.requireLibrary(lib.id);
  }

  async setDefault(nameOrId: string): Promise<Library> {
    const lib = await this.requireLibrary(nameOrId);
    const db = await this.lib.dbHandle();
    await db.update(schema.libraries).set({ isDefault: false }).run();
    await db
      .update(schema.libraries)
      .set({ isDefault: true, updatedAt: new Date().toISOString() })
      .where(eq(schema.libraries.id, lib.id))
      .run();
    return this.requireLibrary(lib.id);
  }

  async removeLibrary(nameOrId: string, opts: { purge?: boolean } = {}): Promise<void> {
    const lib = await this.requireLibrary(nameOrId);
    const db = await this.lib.dbHandle();
    await db.delete(schema.libraries).where(eq(schema.libraries.id, lib.id)).run();
    if (opts.purge && fs.existsSync(lib.path)) {
      fs.rmSync(lib.path, { recursive: true, force: true });
    }
  }

  /**
   * Idempotently ensure a `personal` library exists at `<librariesDir>/personal`.
   * Created silently — one stderr line — on first call when no writable default
   * exists. Returns the library row and a `created` flag.
   */
  async ensurePersonalLibrary(
    librariesDir: string,
  ): Promise<{ library: Library; created: boolean }> {
    const existing = await this.getLibrary("personal");
    if (existing) return { library: existing, created: false };
    // Check whether any writable default already exists.
    const all = await this.listLibraries();
    const writableDefault = all.find(
      (l) => l.isDefault && (l.ownership === "owner" || l.ownership === "contributor"),
    );
    if (writableDefault) return { library: writableDefault, created: false };

    const personalPath = path.join(librariesDir, "personal");
    fs.mkdirSync(personalPath, { recursive: true });
    const lib = await this.addLibrary({
      name: "personal",
      path: personalPath,
      backend: "local",
      backendConfig: {},
      ownership: "owner",
      structure: "domain",
      isDefault: true,
      manifestGenerated: false,
    });
    process.stderr.write(
      `[skaile/library] Created personal library at ${personalPath} (default).\n`,
    );
    return { library: lib, created: true };
  }
}
